import { useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Route as rootRoute } from '../__root';
import { customerQueryOptions } from '../../lib/queries/customers';
import Skills from '../../components/customers/Skills';
import AuditLog from '../../components/customers/AuditLog';
import Usage from '../../components/customers/Usage';
import NetSuite from '../../components/customers/NetSuite';
import Overview from '../../components/customers/Overview';
import { STATUS_STYLE } from '../../lib/status';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/$id',
  component: CustomerDetailPage,
});

const TABS = ['Overview', 'NetSuite', 'Skills', 'Usage', 'Audit Log'] as const;
type Tab = (typeof TABS)[number];

const PLAN_STYLE: Record<string, string> = {
  Enterprise: 'border-amber-500/50 text-amber-400',
  Growth: 'border-blue-500/50 text-blue-400',
  Starter: 'border-white/20 text-muted',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function CustomerDetailPage() {
  const { id } = Route.useParams();
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const { data: tenant, isLoading } = useQuery(customerQueryOptions(id));

  const initials = tenant?.hostAccountId?.slice(0, 1).toUpperCase() ?? '?';
  const pct =
    tenant && tenant.includedTokens > 0
      ? Math.min(
          100,
          Math.round((tenant.periodUsage / tenant.includedTokens) * 100),
        )
      : 0;
  const remaining = tenant ? tenant.includedTokens - tenant.periodUsage : 0;

  return (
    <div className="my-10 min-h-full">
      {/* Tenant header */}
      <div className="px-6 pb-0">
        {isLoading ? (
          <div className="h-16 animate-pulse rounded bg-white/6" />
        ) : tenant ? (
          <div className="flex items-start gap-4">
            <div className="bg-accent flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-lg font-bold text-white">
              {initials}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-primary text-xl font-semibold">
                  {tenant.hostAccountId}
                </h1>
                <span
                  className={[
                    'rounded-full border px-2 py-0.5 text-[0.625rem] capitalize',
                    STATUS_STYLE[tenant.status] ?? 'text-muted border-white/20',
                  ].join(' ')}
                >
                  {tenant.status}
                </span>
                <span
                  className={[
                    'rounded-full border px-2 py-0.5 text-[0.625rem]',
                    PLAN_STYLE[tenant.planName] ?? 'text-muted border-white/20',
                  ].join(' ')}
                >
                  {tenant.planName}
                </span>
              </div>
              <p className="text-muted mt-1 text-xs">
                {tenant.id} · Since {formatDate(tenant.createdAt)}
              </p>
            </div>
          </div>
        ) : null}
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-white/8 px-6 md:pl-20">
        <nav className="flex justify-between md:justify-start md:gap-14">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={[
                'pb-3 text-xs font-medium transition-colors duration-300 ease-in-out md:text-sm',
                activeTab === tab
                  ? 'text-primary border-accent border-b-2'
                  : 'text-primary/8 hover:text-primary',
              ].join(' ')}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="p-6">
        {activeTab === 'Overview' && (
          <Overview
            tenant={tenant}
            isLoading={isLoading}
            pct={pct}
            remaining={remaining}
          />
        )}

        {activeTab === 'NetSuite' && <NetSuite id={id} />}

        {activeTab === 'Skills' && <Skills id={id} />}

        {activeTab === 'Usage' && <Usage id={id} />}

        {activeTab === 'Audit Log' && <AuditLog id={id} />}
      </div>
    </div>
  );
}
